// REACT IMPORTS
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// COMPONENT IMPORTS
import Button from "components/form/Button";
import { valuesProps } from "types/paymentMethods";
import {
  addCard,
  staxProfile,
  useBankData,
  useCardData,
  useOldWalletData,
} from "store/slices/paymentMethodSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  leadData,
  useDashboardData,
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";

// ICONS IMPORTS
import Check, { MobileViewCheck } from "icons/payments/Check";
import { CloseIcon } from "icons/payments/CloseIcon";
import { UserAddIcon } from "icons/payments/UserAddIcon";
import SuccessScreen from "components/ui-component/SuccessScreen";
import BankForm from "./BankForm";
import { useIsLogin } from "store/slices/commonSlice";
import { FieldOption, loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import SecureLock from "icons/payments/SecureLock";
import {
  getIntent,
  getpaymentPermission,
  getPlaidLinkToken,
  getPlaidPaymentToken,
} from "store/slices/paymentsSlice";
import { useParams } from "react-router-dom";
import {
  checkBankManualFormOrPlaid,
  checkDefaultMethodExist,
  decode,
  getBankLogo,
  getBankName,
} from "utils/HelperFunctions";
import { initialValues } from "utils/countryAddressList";
import { PaymentGateway, SettingsValue } from "types/payments";
import { PlaidLinkError, usePlaidLink } from "react-plaid-link";
import ReconnectBankIcon from "icons/payments/ReconnectBankIcon";
import { RootState } from "store/store";
import InputField from "components/form/InputField";
import LoaderRing from "components/ui-component/LoaderRing";
import { useTid } from "utils/testid";
import { resolveStripePublishableKey } from "utils/stripePublishableKey";
import { buildAccountBillingDetails } from "utils/stripeBillingDetails";

// PROPS TYPE
interface AddEditBankFormProps {
  data?: any;
  setData?: any;
  show: boolean;
  setShow: any;
  onCloseClick: any;
  setIsEdit?: any;
  isEdit?: boolean;
}

type LinkFormData = {
  publicToken: string;
  accountId: string;
  bankName: string;
};

/* ------------------------------------- ADD EDIT BANk ------------------------------------- */

const AddEditBankForm = (props: AddEditBankFormProps) => {
  const tid = useTid();
  const {
    data,
    setData,
    show,
    setShow,
    onCloseClick,
    isEdit = false,
    setIsEdit,
  } = props;
  // Dynamically load Stripe when key is available
  const [stripePromise, setStripePromise] = useState<any>(null);
  const { paymentPermission, companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const stripeKey = resolveStripePublishableKey(paymentPermission, "ACH");
  useEffect(() => {
    if (stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    } else {
      setStripePromise(null);
    }
  }, [stripeKey]);
  const portalSettings = usePortalSettingsData();
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const accountLeadData = useLeadData();
  const isLogin = useIsLogin();

  const [bankSecret, setBankSecret] = useState<string | null>(null);
  const [isSecretLoading, setIsSecretLoading] = useState<boolean>(false);
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const onGetLeadData = async () => {
    let lead_payload: any = {
      accounts_id: userData.data.accountData.id,
      cluster_id: userData.data.accountData.clusterid,
      accountid: userData.data.accountData.accountid,
    };
    try {
      await dispatch(leadData(lead_payload)).unwrap();
    } catch (error: any) {
      console.log("onGetLeadData error", error);
    }
  };

  const handleBadgeDisplay = (show?: boolean) => {
    const badge = document.querySelector(".grecaptcha-badge");
    if (badge && badge instanceof HTMLElement) {
      badge.style.display = show ? "block" : "none";
    }
  };

  useEffect(() => {
    if (show) {
      handleBadgeDisplay(true);
    } else {
      handleBadgeDisplay(false);
    }
  }, [show]);

  useEffect(() => {
    if (
      isLogin &&
      accountLeadData &&
      accountLeadData &&
      Object.keys(accountLeadData).length === 0
    ) {
      onGetLeadData();
    }
  }, [isLogin]);

  const [values, setValues] = useState<valuesProps>({
    ...initialValues,
    x_payment_method: "ACH",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bankName, setBankName] = useState<any>("");
  const [bank, setBank] = useState<any>("");
  const [isRingLoading, setIsRingLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [isErr, setIsErr] = useState<string | undefined>(undefined);
  const [numberNotmatchedError, setnumberNotmatchedError] =
    useState<boolean>(false);
  const [successScreen, setSuccessScreen] = useState<boolean>(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLinkTokenLoading, setIsLinkTokenLoading] = useState<boolean>(false);

  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  useEffect(() => {
    if (!show) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const isManualFormAvaiable = useMemo(() => {
    return checkBankManualFormOrPlaid({
      isManualForm: isManualForm,
      manual_ach: portalSettings?.manual_ach,
      validate_bank_account: portalSettings?.validate_bank_account,
    });
  }, [isManualForm, portalSettings]);

  const onClose = () => {
    setShow(false);
    setIsEdit(false);
    setData(null);
    setValues(initialValues);
    setSuccessScreen(false);
    setBank(false);
    setBankName(false);
    setErrMsg(undefined);
    setnumberNotmatchedError(false);
  };

  useEffect(() => {
    if (!isEdit) return;
    // setValues({
    //   ...values,
    //   routing_number: data?.routing_number || "",
    //   account_number: data?.account_number || "",
    //   r_account_number: data?.account_number || "",
    // });
    setIsManualForm(false);
    // findBankByRoutingNumber(data?.routing_number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const handleChange = (e: any, name: string) => {
    setIsErr(undefined);
    if (name === "routing_number") {
      if (e.target.value.length < 10) {
        setValues({ ...values, [name]: e.target.value });
        setBankName("");
        setBank("");
        setErrMsg(undefined);
        if (e.target.value.length === 9) {
          findBankByRoutingNumber(e.target.value);
        }
      }
    } else if (name === "r_account_number") {
      setnumberNotmatchedError(false);
      setValues({ ...values, [name]: e.target.value });
      if (values?.account_number?.length === e.target.value?.length) {
        setnumberNotmatchedError(values?.account_number !== e.target.value);
      }
    } else {
      setValues({ ...values, [name]: e.target.value });
    }
  };

  function checkNumberDigits(num: any, limit: number) {
    const inputStr = num.toString();

    const digitStr = inputStr.replace(/[^0-9]/g, "");
    const digitCount = digitStr.length;

    if (digitCount > limit) {
      const last14Digits = digitStr.slice(-14);
      return `...${last14Digits}`;
    } else {
      return num;
    }
  }

  const findBankByRoutingNumber = async (routingNumber: string) => {
    setIsRingLoading(true);
    try {
      let bankObj = await getBankName(routingNumber);
      if (bankObj?.type === "success") {
        setBankName(bankObj?.bank_name);
        setBank(bankObj?.bank_name);
      } else {
        setErrMsg("Invalid routing number");
        setIsRingLoading(false);
      }
    } catch (error) {
      console.log("error =====", error);
      setErrMsg("Invalid routing number");
      setIsRingLoading(false);
    } finally {
      setIsRingLoading(false);
    }
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    let payload: valuesProps = {
      ...values,
      x_payment_method: "ACH",
      cluster_id: clusterId || userData?.data?.accountData?.clusterid,
      accountid: userData?.data?.accountData?.accountid,
      accounts_id: userData?.data?.accountData?.id,
      x_first_name: userData?.data?.accountData?.first_name,
      x_last_name: userData?.data?.accountData?.last_name,
      x_phone: userData?.data?.accountData?.phone,
      x_email: userData?.data?.accountData?.email,
      lead_id: accountLeadData?.lead_id,
      gateway_id: accountLeadData?.gatewayIdACH,
      gateway_name: accountLeadData?.ach_gateway_name,
      account_holder_name: values?.choldername,
      bank_name: bank,
      x_address: userData?.data?.accountData?.address1,
      address2: userData?.data?.accountData?.address2,
      x_city: userData?.data?.accountData?.city,
      x_state: userData?.data?.accountData?.state,
      x_zip: userData?.data?.accountData?.postal_code,
      x_country: userData?.data?.accountData?.country,
      isManualACH: true,
      default: "Y",
      default_profile: "N",
      // default: checkDefaultMethodExist(cardList, bankList, oldWalletData),
      // default_profile: checkDefaultMethodExist(
      //   cardList,
      //   bankList,
      //   oldWalletData
      // ),
    };
    delete payload["r_account_number"];

    if (isEdit) {
      onEditBank(payload);
    } else {
      onCreateBank(payload);
    }
  };

  const onCreateBank = async (payload: valuesProps) => {
    setIsLoading(true);
    try {
      const res: any = await dispatch(addCard(payload)).unwrap();
      if (res && res?.body?.success) {
        setSuccessScreen(true);
      } else {
        setIsErr(res?.body?.data?.msg);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("create card err: -->", error?.message);
    }
  };

  const onEditBank = async (val: valuesProps) => {
    setIsLoading(true);
    try {
      let payload = { ...val };
      payload["isUpdate"] = true;
      payload["client_id"] = data?.id;
      const res: any = await dispatch(addCard(payload)).unwrap();
      if (res && res?.body?.success) {
        setSuccessScreen(true);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("Edit card err: -->", error?.message);
    }
  };

  const getSecret = async () => {
    try {
      setIsSecretLoading(true);
      let payload = {
        name:
          (userData?.data?.accountData?.first_name || "") +
          (userData?.data?.accountData?.last_name || ""),
        email: userData?.data?.accountData?.email,
        phone_number: userData?.data?.accountData.phone,
        description: "Online Payment",
        payment_note: "One-time",
        currency: "usd",
        payment_method_types: "us_bank_account",
        isRecurring: true,
        isWalletProfile: true,
        accountid: userData?.data?.accountData?.accountid,
        cluster_id: clusterId,
      };
      let res: any = await dispatch(getIntent(payload));
      if (res?.payload?.body?.success) {
        setBankSecret(res?.payload?.body?.data?.client_secret);
      }
      setIsSecretLoading(false);
    } catch (err) {
      console.log("err =====", err);
      setIsSecretLoading(false);
    }
  };

  const getLinkToken = async () => {
    setIsLinkTokenLoading(true);
    try {
      let payload = {
        client_name:
          (companyDetails?.name || companyDetails?.company_name) ??
          "Palm Labs LLC",
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name?.trim(),
        email: userData?.data?.accountData?.email,
        clusterid: userData?.data?.accountData?.clusterid,
        slug: cluster_id || "",
      };
      let res: any = await dispatch(getPlaidLinkToken(payload)).unwrap();
      if (res?.body?.success) {
        setLinkToken(res?.body?.data?.link_token || null);
      }
    } catch (error) {
      console.log("getLinkToken error =====", error);
    } finally {
      setIsLinkTokenLoading(false);
    }
  };

  useEffect(() => {
    if (
      show &&
      !bankSecret &&
      !isSecretLoading &&
      !successScreen &&
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE
    ) {
      getSecret();
    }
    if (
      show &&
      !successScreen &&
      paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE &&
      !linkToken &&
      isManualFormAvaiable
    ) {
      getLinkToken();
    }
  }, [show, portalSettings, paymentPermission, isManualForm]);

  const getPaymentMethodPermission = async () => {
    try {
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        clusterid: clusterId,
        lead_id: accountLeadData?.lead_id,
      };
      await dispatch(getpaymentPermission(payload));
    } catch (error) {
      console.log("getPaymentMethodPermission error =====", error);
    }
  };

  useEffect(() => {
    if (isLogin && show) {
      getPaymentMethodPermission();
    }
  }, [isLogin]);

  if (successScreen) {
    return (
      <div data-testid={tid("bank-form-success")}>
        <SuccessScreen
          closeLabel="Close bank confirmation"
          title={isEdit ? "Payment Method Updated" : "Payment Method Added"}
          content={
            isEdit ? (
              <>
                Your payment method details have been successfully updated.{" "}
                <br />
                You’re all set for your next transactions.
              </>
            ) : (
              <>
                Thank you for adding a new payment method to your account. You
                can now use it to make payments.
              </>
            )
          }
          onCloseClick={() => {
            onClose();
            onCloseClick();
          }}
        />
      </div>
    );
  }

  const bankFormOptions = {
    clientSecret: bankSecret || "",
    appearance: {
      variables: {
        fontFamily: "DM Sans, sans-serif",
        colorDanger: "#ff0000",
        fontSizeSm: "12px",
        fontSizeXs: "12px",
        colorText: "#19224c",
        colorTextSecondary: "#19224c",
        colorPrimary: "#1678f2",
        colorBackgroundDeemphasize10: "#dfe4ec",
        borderRadius: "6px",
      },
      rules: {
        ".Label": {
          color: "#8c90a5",
          fontWeight: "700",
          fontSize: "12px",
          textTransform: "uppercase",
          display: "none",
        },
        ".Input::placeholder": {
          fontWeight: "400",
          fontSize: "12px",
          color: "#8c90a5",
        },
        ".Input": {
          border: "1px solid #dfe4ec",
          fontWeight: "400",
          fontSize: "12px",
          color: "#8c90a5",
        },
        ".Error": {
          fontSize: "12px",
          fontWeight: "400",
        },
        ".PickerItem": {
          border: "1px solid #dfe4ec",
          borderRadius: "6px",
        },
      },
    },
  };

  const handleAddOrEditBank = (data: any) => {
    try {
      let payload: valuesProps = {
        x_payment_method: "ACH",
        routing_number: data?.routing_numebr,
        account_number: data?.account_number,
        gateway_name: data?.gateway_name,
        x_city: data?.x_city ?? "",
        x_address: data?.x_address ?? "",
        address2: data?.address2 ?? "",
        x_zip: data?.x_zip ?? "",
        x_state: data?.x_state ?? "",
        x_country: data?.x_country ?? "",
        cluster_id: clusterId || userData?.data?.accountData?.clusterid,
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        x_first_name: userData?.data?.accountData?.first_name,
        x_last_name: userData?.data?.accountData?.last_name,
        x_phone: userData?.data?.accountData?.phone,
        x_email: userData?.data?.accountData?.email,
        lead_id: accountLeadData?.lead_id,
        gateway_id: accountLeadData?.gatewayIdACH,
        account_holder_name: userData?.data?.accountData?.first_name,
        bank_name: data.bankName,
        default: "Y",
        default_profile: "N",
        // default: checkDefaultMethodExist(cardList, bankList, oldWalletData),
        // default_profile: checkDefaultMethodExist(
        //   cardList,
        //   bankList,
        //   oldWalletData
        // ),
      };
      if (isEdit) {
        onEditBank(payload);
      } else {
        onCreateBank(payload);
      }
    } catch (error) {
      console.log("handleAddOrEditPlaid error =====", error);
    }
  };

  return (
    <div
      className="bg-bgPrimary h-full overflow-y-auto lg:pt-[70px] pt-[56px] select-none"
      data-testid={tid(isEdit ? "edit-bank-form" : "add-bank-form")}
      aria-label={isEdit ? "Reconnect bank form" : "Add bank form"}
    >
      <div
        className="lg:px-10 px-5 lg:py-5 py-3 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-10"
        data-testid={tid("bank-form-header")}
      >
        <div className="flex items-center gap-2">
          {isEdit ? (
            <ReconnectBankIcon aria-hidden="true" />
          ) : (
            <UserAddIcon aria-hidden="true" />
          )}
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            {isEdit ? "Reconnect bank" : "Add new bank"}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div
            role="button"
            tabIndex={0}
            aria-label="Close bank form"
            data-testid={tid("bank-form-close-button")}
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon aria-hidden="true" />
          </div>
        </div>
      </div>
      {paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE ? (
        <>
          {bankSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={bankFormOptions}>
              <StripeBankForm isEdit={isEdit} cppId={data?.id} />
            </Elements>
          ) : (
            <div className="lg:px-10 px-5 py-5">
              <Button
                text={`Add Bank`}
                data-testid={tid("bank-form-provider-loading-button")}
                aria-label="Add bank"
                className="!w-full mt-5"
                isDisabled={true}
                isLoading={isSecretLoading}
              />
            </div>
          )}
        </>
      ) : paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX ? (
        <StaxBankForm
          handleChange={handleChange}
          numberNotmatchedError={numberNotmatchedError}
          errMsg={errMsg}
          bankName={bankName}
          isRingLoading={isRingLoading}
          values={values}
          error={isErr}
          setnumberNotmatchedError={setnumberNotmatchedError}
          checkNumberDigits={checkNumberDigits}
          setSuccessScreen={setSuccessScreen}
          show={show}
        />
      ) : isManualFormAvaiable ? (
        <PlaidBankForm
          isLinkTokenLoading={isLinkTokenLoading}
          linkToken={linkToken}
          setIsManualForm={setIsManualForm}
          isEdit={isEdit}
          handleAddOrEditBank={handleAddOrEditBank}
          setSuccessScreen={setSuccessScreen}
        />
      ) : (
        <form onSubmit={onSubmit} data-testid={tid("bank-form-fields")}>
          <div className="lg:p-10 flex flex-col gap-5 items-start p-5">
            <div className="relative">
              <span className="hidden lg:block">
                <Check />
              </span>
              <span className="block lg:hidden">
                <MobileViewCheck />
              </span>
              <div className="flex justify-between items-center lg:px-14 px-8 absolute w-full top-[65%]">
                <div className="w-[30%] text-sm lg:text-lg text-txtSecondary leading-normal font-medium">
                  {checkNumberDigits(values?.routing_number, 10)}
                </div>
                <div className="lg:w-[55%] w-[50%] text-sm lg:text-lg text-txtSecondary leading-normal font-medium">
                  {checkNumberDigits(
                    values?.account_number,
                    window.innerWidth < 500 ? 14 : 18
                  )}
                </div>
                <div className="w-[10%] text-sm lg:text-lg text-txtSecondary leading-normal font-medium">
                  1234
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-5 items-start w-full">
              <BankForm
                handleChange={handleChange}
                numberNotmatchedError={numberNotmatchedError}
                errMsg={errMsg}
                bankName={bankName}
                isRingLoading={isRingLoading}
                values={values}
                error={isErr}
                setnumberNotmatchedError={setnumberNotmatchedError}
              />
              {portalSettings?.validate_bank_account === SettingsValue.YES && (
                <div>
                  <p
                    role="button"
                    tabIndex={0}
                    aria-label="Connect bank account instead"
                    data-testid={tid("bank-form-connect-bank-button")}
                    className="mt-2 cursor-pointer text-blue font-medium hover:!text-btnPrimaryHover text-xs"
                    onClick={() => setIsManualForm(false)}
                  >
                    Connect your bank account instead
                  </p>
                  {isErr && <div className="text-xs text-red">{isErr}</div>}
                </div>
              )}
            </div>
            <Button
              text={isEdit ? "Update Bank" : "Add Bank"}
              data-testid={tid("bank-form-submit-button")}
              aria-label={isEdit ? "Update bank" : "Add bank"}
              className="w-full"
              type="submit"
              isLoading={isLoading}
              isDisabled={
                isRingLoading ||
                values?.choldername === "" ||
                values?.account_number === "" ||
                values?.routing_number === "" ||
                values?.routing_number?.length !== 9 ||
                values?.r_account_number === "" ||
                numberNotmatchedError ||
                !!errMsg ||
                !!isErr
              }
            />
          </div>
        </form>
      )}
    </div>
  );
};

const PlaidBankForm = ({
  isLinkTokenLoading,
  linkToken,
  setIsManualForm,
  isEdit,
  handleAddOrEditBank,
  setSuccessScreen,
}: {
  isLinkTokenLoading: boolean;
  linkToken: string | null;
  setIsManualForm: (val: boolean) => void;
  isEdit: boolean;
  handleAddOrEditBank: (data: any) => void;
  setSuccessScreen: (val: boolean) => void;
}) => {
  const portalSettings = usePortalSettingsData();

  return (
    <div className="lg:px-10 py-5">
      {portalSettings?.manual_ach === SettingsValue.YES && (
        <div className="mb-4 text-xs leading-[15.62px] w-fit">
          <span
            className="text-blue cursor-pointer font-medium hover:!text-btnPrimaryHover"
            onClick={() => setIsManualForm(true)}
          >
            Enter bank details manually instead
          </span>{" "}
          <span>(takes 1 - 2 business days)</span>
        </div>
      )}

      {!linkToken ? (
        <Button
          text={!isEdit ? "Add Bank" : "Update Bank"}
          className={"!w-full mt-5"}
          isDisabled={true}
          isLoading={isLinkTokenLoading}
          variant={"variable"}
        />
      ) : (
        <LinkForm
          isLinkTokenLoading={isLinkTokenLoading}
          linkToken={linkToken}
          isEdit={isEdit}
          handleAddOrEditBank={handleAddOrEditBank}
          setSuccessScreen={setSuccessScreen}
        />
      )}
    </div>
  );
};

const LinkForm = ({
  isLinkTokenLoading,
  linkToken,
  isEdit,
  handleAddOrEditBank,
  setSuccessScreen,
}: {
  isLinkTokenLoading: boolean;
  linkToken: string | null;
  isEdit: boolean;
  handleAddOrEditBank: (data: any) => void;
  setSuccessScreen: (val: boolean) => void;
}) => {
  const [isPlaidTokenLoading, setIsPlaidTokenLoading] =
    useState<boolean>(false);

  const { clusterId: cluster_id } = useParams();
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const getPaymentToken = async (data: LinkFormData) => {
    setIsPlaidTokenLoading(true);
    try {
      let payload = {
        public_token: data.publicToken,
        bankAccountID: data.accountId,
        clusterid: userData?.data?.accountData?.clusterid,
        accountid: userData?.data?.accountData?.accountid,
        slug: cluster_id || "",
      };
      let res: any = await dispatch(getPlaidPaymentToken(payload)).unwrap();
      if (res?.body?.success) {
        const { address } = res?.body?.data;
        let values = {
          bankName: data?.bankName,
          bank: data?.bankName,
          choldername: userData?.data?.accountData?.first_name,
          routing_number: res?.body?.data?.routing_numebr,
          account_number: res?.body?.data?.account_number,
          gateway_name: res?.body?.data?.gateway_name,
          x_city: address?.city ?? "",
          x_address: address?.street ?? "",
          address2: "",
          x_zip: address?.postal_code ?? "",
          x_state: address?.region ?? "",
          x_country: address?.country ?? "",
          email: userData?.data?.accountData?.email,
          gateway_id: res?.body?.data?.gatewayId,
        };
        let isCCTokenAvailable =
          res?.body?.data?.cc_token &&
          res?.body?.data?.cc_token?.trim() !== "" &&
          res?.body?.data?.gateway_name === PaymentGateway.ACHQ;

        if (isCCTokenAvailable) {
          setSuccessScreen(true);
        } else {
          await handleAddOrEditBank(values);
        }
      } else {
        setSuccessScreen(false);
      }
    } catch (error) {
      console.log("getPaymentToken error =====", error);
      setSuccessScreen(false);
    } finally {
      setIsPlaidTokenLoading(false);
    }
  };

  const onSuccess = useCallback(
    async (public_token?: string, metadata?: any) => {
      if (public_token && metadata.account_id) {
        let data = {
          publicToken: public_token,
          accountId: metadata.account_id,
          bankName: metadata.institution.name,
        };
        await getPaymentToken(data);
      }
    },
    []
  );

  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken!,
    onSuccess: onSuccess,
    onExit: (error: PlaidLinkError | null) => {
      console.log("error =====", error);
    },
  };

  const { open, ready } = usePlaidLink(config);

  return (
    <Button
      text={!isEdit ? "Add Bank" : "Update Bank"}
      className={"!w-full mt-5"}
      isDisabled={!ready || isLinkTokenLoading}
      onClick={() => open()}
      isLoading={isPlaidTokenLoading}
      variant={"variable"}
    />
  );
};

const StripeBankForm = ({
  isEdit,
  cppId = 0,
}: {
  isEdit: boolean;
  cppId?: number;
}) => {
  const { companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const stripe = useStripe();
  const elements = useElements();
  const userData = useUserData();

  const paymentElementOptions: {
    fields: {
      billingDetails: {
        name?: FieldOption;
        email?: FieldOption;
      };
    };
  } = {
    fields: {
      billingDetails: {
        name: "never",
        email: "never",
      },
    },
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (!stripe || !elements) {
        return;
      }

      const billingDetails = buildAccountBillingDetails({
        account: userData?.data?.accountData,
        email: email,
        nameFallback: email,
      });

      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.href}?payment_method=ACH&checked=false&isEdit=${isEdit}&cppId=${cppId}`,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
      });
      setIsLoading(false);

      if (error) {
        console.log(error.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.log("error =====", error);
      setIsLoading(false);
    }
  };

  // Save is enabled once the bank details are complete. The element reports
  // its own changes through onChange. Looking it up after mount instead
  // assumed it already existed, and crashed the page when it did not.
  const handlePaymentElementChange = (e: any) => {
    if (!e.empty && e.complete) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  useEffect(() => {
    if (userData?.data?.accountData?.email) {
      setEmail(userData?.data?.accountData?.email || "");
    }
  }, [userData]);

  return (
    <div className="lg:px-10 px-5 pt-4">
      <div className="relative">
        <div
          className={`w-full absolute ${
            isDisabled ? "-top-2" : "top-2"
          } right-0  flex justify-between items-center text-txtSecondary`}
        >
          {isDisabled ? (
            <div className="text-xs font-bold leading-[15.62px] text-txtSecondary  uppercase">
              Bank Account
            </div>
          ) : (
            <div></div>
          )}
          <span className="text-xs text-txtSecondary font-medium flex gap-1 items-center justify-center">
            <SecureLock />
            Secured by {companyDetails?.name || companyDetails?.company_name}
          </span>
        </div>
        <div>
          <PaymentElement
            options={paymentElementOptions}
            onChange={handlePaymentElementChange}
          />
        </div>
      </div>

      <div className="w-full py-5">
        <div className="text-xs font-bold leading-[18px] text-txtSecondary mb-2 mt-3">
          EMAIL
        </div>
        <div className="relative w-full ">
          <input
            autoComplete="off"
            className={`w-full border-strokePrimary rounded-[5px] border py-2 px-[15px] bg-bgPrimary text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none `}
            placeholder="Email address"
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            value={email}
          />
        </div>
      </div>

      <Button
        text={`Add Bank`}
        className="!w-full mt-5"
        onClick={handleSubmit}
        isDisabled={isDisabled || email.trim() === ""}
        isLoading={isLoading}
      />
    </div>
  );
};

interface BankFormProps {
  handleChange: (e: any, value: string) => void;
  values: valuesProps;
  numberNotmatchedError: boolean;
  bankName: string;
  errMsg: string | undefined;
  isRingLoading: boolean;
  disableInputFields?: boolean;
  error?: string | undefined;
  setnumberNotmatchedError: (val: boolean) => void;
  checkNumberDigits: (value: any, length: number) => string;
  setSuccessScreen: (val: boolean) => void;
  show?: boolean;
}

const StaxBankForm = ({
  handleChange,
  values,
  numberNotmatchedError,
  bankName,
  errMsg,
  isRingLoading,
  disableInputFields = false,
  error = undefined,
  setnumberNotmatchedError,
  checkNumberDigits,
  setSuccessScreen,
  show,
}: BankFormProps) => {
  const [logo, setLogo] = useState<any>(null);
  const [isErr, setIsErr] = useState<boolean>(false);
  const [tokenErr, setTokenErr] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userData = useUserData();
  const accountLeadData = useLeadData();
  const dispatch = useAppDispatch();
  const staxRef = useRef<any>(null);
  const { paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );

  const scriptLoaded = useRef<boolean>(false);

  const initStax = () => {
    const token = paymentPermission?.ach_gateway_name?.key;
    const config = {};
    const instance = new window.StaxJs(token, config);

    if (!instance) {
      return;
    }
    staxRef.current = instance;
  };

  useEffect(() => {
    if (scriptLoaded.current || !show) return;
    const script = document.createElement("script");
    script.src = "https://staxjs.staxpayments.com/staxjs-captcha.js";
    script.async = true;

    script.onload = () => initStax();

    document.body.appendChild(script);
    scriptLoaded.current = true;

    return () => {
      document.body.removeChild(script);
    };
  }, [show]);

  useEffect(() => {
    if (bankName) {
      let logo = getBankLogo({ bankName, type: "small" });
      setLogo(logo || null);
    }
  }, [bankName]);

  const isDisabled = useMemo(() => {
    const isTokenErr = !!tokenErr;
    return (
      isRingLoading ||
      values?.choldername === "" ||
      values?.account_number === "" ||
      values?.routing_number === "" ||
      values?.routing_number?.length !== 9 ||
      values?.r_account_number === "" ||
      isTokenErr ||
      numberNotmatchedError ||
      !!errMsg ||
      !!isErr
    );
  }, [isRingLoading, values, tokenErr, numberNotmatchedError, errMsg, isErr]);

  const handleSubmit = async (e: any) => {
    setIsLoading(true);
    try {
      const [month, year] = values?.x_exp_date?.split("/") || ["", ""];
      const namesSplit = values?.choldername?.trim().split(" ") || [];

      let payload: any = {
        firstname: namesSplit?.[0] || userData?.data?.accountData?.first_name,
        lastname: namesSplit?.[1] || userData?.data?.accountData?.last_name,
        bank_name: values?.bank_name || bankName,
        bank_account: values.account_number,
        bank_routing: values.routing_number,
        person_name:
          values?.choldername ||
          `${userData?.data?.accountData?.first_name} ${userData?.data?.accountData?.last_name}`,

        method: "bank",
        validate: false,
        bank_type: "checking",
        bank_holder_type: "personal",
        email: userData?.data?.accountData?.email,
        phone: userData?.data?.accountData?.phone,
      };

      if ((userData?.data?.accountData?.country || "USA")?.length > 3) {
        payload = {
          ...payload,
          address_country: userData?.data?.accountData?.country ?? "USA",
        };
      }
      if (userData?.data?.accountData?.city?.length > 0) {
        payload = {
          ...payload,
          address_city: userData?.data?.accountData?.city ?? "",
        };
      }
      if (userData?.data?.accountData?.postal_code?.length > 0) {
        payload = {
          ...payload,
          address_zip: userData?.data?.accountData?.postal_code ?? "",
        };
      }
      if (userData?.data?.accountData?.state?.length > 0) {
        payload = {
          ...payload,
          address_state: userData?.data?.accountData?.state ?? "",
        };
      }
      if (userData?.data?.accountData?.address1?.length > 0) {
        payload = {
          ...payload,
          address_1: userData?.data?.accountData?.address1 ?? "",
        };
      }
      if (userData?.data?.accountData?.address2?.length > 0) {
        payload = {
          ...payload,
          address_2: userData?.data?.accountData?.address2 ?? "",
        };
      }

      const result = await staxRef.current.tokenize(payload);

      let dataObj = {
        cc_token: result?.id,
        cc_token2: result?.customer_id,
        lead_id: accountLeadData?.lead_id,
        email: userData?.data?.accountData?.email,
        module: "payment_method",
        x_method: "CC",
        clusterid: userData?.data?.accountData?.clusterid,
        accountid: userData?.data?.accountData?.accountid,
      };

      const response: any = await dispatch(staxProfile(dataObj)).unwrap();
      if (response?.body?.success) {
        setSuccessScreen(true);
      } else {
        setTokenErr(response?.body?.data?.message || null);
      }
    } catch (err: any) {
      console.log("bank error===", err);
      const error = err.fieldErrors || [];
      if (error?.length > 0) {
        setTokenErr(error[0]?.message || "Card details error");
        setErrorField(error[0]?.field || null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form>
      <div className="lg:p-10 flex flex-col gap-5 items-start p-5">
        <div className="relative">
          <span className="hidden lg:block">
            <Check />
          </span>
          <span className="block lg:hidden">
            <MobileViewCheck />
          </span>
          <div className="flex justify-between items-center lg:px-14 px-8 absolute w-full top-[65%]">
            <div className="w-[30%] text-sm lg:text-lg text-txtSecondary leading-normal font-medium">
              {checkNumberDigits(values?.routing_number, 10)}
            </div>
            <div className="lg:w-[55%] w-[50%] text-sm lg:text-lg text-txtSecondary leading-normal font-medium">
              {checkNumberDigits(
                values?.account_number,
                window.innerWidth < 500 ? 14 : 18
              )}
            </div>
            <div className="w-[10%] text-sm lg:text-lg text-txtSecondary leading-normal font-medium">
              1234
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full lg:gap-[30px] gap-5">
          <div className="w-full">
            <InputField
              name="choldername"
              label="Account holder name"
              className="!py-[10px] !rounded-[7px]"
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Full name of account holder"
              value={values?.choldername}
              onChange={(e) => handleChange(e, "choldername")}
              disabled={disableInputFields}
            />
          </div>
          <div className="w-full">
            <InputField
              type="number"
              name="account_number"
              label="Account Number"
              className={`!py-[10px] !rounded-[7px] ${
                numberNotmatchedError || error ? "!border-red" : ""
              }`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Enter Account Number"
              value={values?.account_number}
              onChange={(e) => {
                handleChange(e, "account_number");
                setnumberNotmatchedError(false);
              }}
              disabled={disableInputFields}
            />
          </div>
          <div className="w-full">
            <InputField
              type="number"
              name="r_account_number"
              label="Re-enter Account Number"
              className={`!py-[10px] !rounded-[7px] ${
                numberNotmatchedError || error ? "!border-red" : ""
              }`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Re-enter Account Number"
              value={values?.r_account_number}
              onChange={(e) => {
                handleChange(e, "r_account_number");
                setnumberNotmatchedError(false);
              }}
              error={
                numberNotmatchedError
                  ? "Account number are not matched"
                  : undefined
              }
              onBlur={() => {
                if (values?.r_account_number !== values?.account_number) {
                  setnumberNotmatchedError(true);
                }
              }}
              disabled={disableInputFields}
            />
          </div>
          <div className="w-full relative">
            <InputField
              type="number"
              name="routing_number"
              label="Routing Number"
              className={`!py-[10px] !rounded-[7px] ${error ? "!border-red" : ""}`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Enter Routing Number"
              value={values?.routing_number}
              onChange={(e) => handleChange(e, "routing_number")}
              error={errMsg}
              disabled={disableInputFields}
            />
            {bankName && logo && !isRingLoading && (
              <div className="absolute right-[15px] top-[50%]">{logo}</div>
            )}
            {!errMsg && bankName && (
              <div className="absolute top-[100%] left-0">
                <div
                  className={`mt-[3px] text-xs leading-normal text-txtSecondary capitalize`}
                >
                  {bankName?.toLowerCase()}
                </div>
              </div>
            )}
            {isRingLoading && (
              <div className="absolute right-[24px] top-[50%]">
                <div className="text-xs leading-[16px] text-gray1 ">
                  <LoaderRing exsm />
                </div>
              </div>
            )}
          </div>
        </div>
        <Button
          text="Add Bank"
          className="!w-full mt-5"
          isDisabled={isDisabled}
          isLoading={isLoading}
          onClick={handleSubmit}
        />
      </div>
    </form>
  );
};

export default AddEditBankForm;
